import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Loader from '../../components/Common/Loader';
import { Search, TrendingUp, TrendingDown } from 'lucide-react';

interface OPCVMItem {
    id: number;
    nom_fonds: string;
    type_fonds: string | null;
    societe_gestion: string | null;
    depositaire: string | null;
    valeur_origine: number | null;
    // Cotation fields
    vl_precedente: number | null;
    date_vl_precedente: string | null;
    valeur_liquidative: number | null;
    date_vl: string | null;
    variation_origine_pct: number | null;
    variation_periode_pct: number | null;
}

// Category options with clear descriptions
const CATEGORY_OPTIONS = [
    { value: '', label: 'Toutes catégories' },
    { value: 'A', label: 'A - Actions' },
    { value: 'OCT', label: 'OCT - Obligations Court Terme' },
    { value: 'OMLT', label: 'OMLT - Obligations Moyen/Long Terme' },
    { value: 'OATC', label: 'OATC - Obligations à Taux Constant' },
    { value: 'C', label: 'C - Contractuels' },
    { value: 'D', label: 'D - Diversifiés' },
    { value: 'M', label: 'M - Monétaires' },
];

const OPCVMScreen: React.FC = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [opcvmList, setOpcvmList] = useState<OPCVMItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    useEffect(() => {
        if (token) fetchOPCVM();
    }, [token, selectedCategory]);

    const fetchOPCVM = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category', selectedCategory);
            if (searchTerm) params.append('search', searchTerm);

            const response = await fetch(`${API_URL}/v1/opcvm?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setOpcvmList(data);
            }
        } catch (error) {
            console.error('Error fetching OPCVM:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchOPCVM();
    };

    const filteredList = opcvmList.filter(item =>
        item.nom_fonds?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <SiteLayout>
            <Header title="OPCVM - Fonds d'Investissement" />
            <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
                {/* Filters */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Search */}
                    <form onSubmit={handleSearch} style={{ flex: 1, minWidth: '250px' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input
                                type="text"
                                placeholder="Rechercher un fonds..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 12px 12px 44px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                }}
                            />
                        </div>
                    </form>

                    {/* Category Filter Dropdown */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: 500 }}>
                            Catégorie
                        </label>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{
                                padding: '10px 14px',
                                borderRadius: '10px',
                                border: '1px solid #e5e7eb',
                                fontSize: '14px',
                                background: 'var(--bg-box)',
                                cursor: 'pointer',
                                minWidth: '280px',
                            }}
                        >
                            {CATEGORY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Results count */}
                    <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>
                        {filteredList.length} fonds
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <Loader message="Chargement des OPCVM..." />
                ) : (
                    <div style={{ background: 'var(--bg-box)', borderRadius: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
                            <thead>
                                <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                    <th style={{ color: 'white', padding: '14px 10px', textAlign: 'left', fontSize: '13px' }}>Nom du Fonds</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Cat.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>VL Préc.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>VL Actuelle</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Date VL</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Var. Orig.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Var. J.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((item) => (
                                    <tr
                                        key={item.id}
                                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                        onClick={() => navigate(`/opcvm/${item.id}`)}
                                    >
                                        <td style={{ padding: '12px 10px', fontWeight: 600, fontSize: '13px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nom_fonds}</td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                            <span style={{
                                                padding: '3px 10px',
                                                borderRadius: '10px',
                                                background: '#f0f4ff',
                                                color: '#667eea',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                            }}>
                                                {item.type_fonds || '-'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                                            {item.vl_precedente?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, fontSize: '13px' }}>
                                            {item.valeur_liquidative?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
                                            {item.date_vl ? new Date(item.date_vl).toLocaleDateString('fr-FR') : '-'}
                                        </td>
                                        <td style={{
                                            padding: '12px 8px',
                                            textAlign: 'right',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            color: (item.variation_origine_pct ?? 0) >= 0 ? '#10b981' : '#ef4444',
                                        }}>
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                                {(item.variation_origine_pct ?? 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                                {item.variation_origine_pct?.toFixed(2) || '-'}%
                                            </span>
                                        </td>
                                        <td style={{
                                            padding: '12px 8px',
                                            textAlign: 'right',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            color: (item.variation_periode_pct ?? 0) >= 0 ? '#10b981' : '#ef4444',
                                        }}>
                                            {item.variation_periode_pct !== null ? `${item.variation_periode_pct >= 0 ? '+' : ''}${item.variation_periode_pct.toFixed(2)}%` : '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/opcvm/${item.id}`); }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    background: '#f0f4ff',
                                                    color: '#667eea',
                                                    cursor: 'pointer',
                                                    fontWeight: 500,
                                                    fontSize: '12px',
                                                }}
                                            >
                                                Détails
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredList.length === 0 && (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                Aucun OPCVM trouvé
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default OPCVMScreen;

